import React, {useMemo, useState} from 'react';
import {Box, Stack, Tooltip, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {
	IRemovedStopDiff,
	IStopDiffRow,
	IStopsDiff
} from "@containers/singleVoyage/voyageTemplates/sync/helpers/buildStopsDiff";
import {
	ISyncChangeDetailVoyageTemplate,
	SyncChangeTypeVoyageTemplate
} from "@containers/singleVoyage/voyageTemplates/types";
import {sx, STOP_COLOR_SYNC} from './stopsLineSx'
import {IHighlightedStopSync} from "@containers/singleVoyage/voyageTemplates/sync/map/SyncMapVoyageTemplate";

/** Horizontal room per stop on the line. */
const STEP = 78
const PAD_X = 40
const LINE_Y = 52
/** How far a changed stop bends the line away from it. */
const DEVIATION = 24
const RADIUS = 14
const HEIGHT = 128
/** Shortest run of untouched stops worth folding into one node. */
const MIN_FOLDED_RUN = 3

type LineNode =
	| { kind: 'stop', row: IStopDiffRow }
	| { kind: 'removed', removed: IRemovedStopDiff }
	| { kind: 'folded', rows: IStopDiffRow[] }

const isTouched = (row: IStopDiffRow) => !!row.change || row.wasOrdinal !== null

/** Removed stops go back where they stood in the voyage, among the template's stops. */
const placeRemoved = (rows: IStopDiffRow[], removed: IRemovedStopDiff[]): LineNode[] => {
	const pending = removed.slice().sort((a, b) => (a.wasOrdinal ?? Infinity) - (b.wasOrdinal ?? Infinity))
	const nodes: LineNode[] = []
	rows.forEach((row) => {
		const oldPosition = row.change?.type === 'ADD' ? null : (row.wasOrdinal ?? row.ordinal)
		while (pending.length && oldPosition !== null && (pending[0].wasOrdinal ?? Infinity) < oldPosition) {
			nodes.push({kind: 'removed', removed: pending.shift()})
		}
		nodes.push({kind: 'stop', row})
	})
	pending.forEach((it) => nodes.push({kind: 'removed', removed: it}))
	return nodes
}

const foldUntouched = (nodes: LineNode[], expanded: number[]): LineNode[] => {
	const result: LineNode[] = []
	let run: IStopDiffRow[] = []
	const flush = () => {
		if (run.length >= MIN_FOLDED_RUN && !expanded.includes(run[0].ordinal)) {
			result.push({kind: 'folded', rows: run})
		} else {
			run.forEach((row) => result.push({kind: 'stop', row}))
		}
		run = []
	}
	nodes.forEach((node) => {
		if (node.kind === 'stop' && !isTouched(node.row)) {
			run.push(node.row)
			return
		}
		flush()
		result.push(node)
	})
	flush()
	return result
}

/** Expanded runs, as x ranges, so each can be folded back from a bracket over it. */
const expandedRanges = (nodes: LineNode[], rows: IStopDiffRow[], expanded: number[]) => {
	return expanded.map((first) => {
		const run: number[] = []
		let index = rows.findIndex((row) => row.ordinal === first)
		while (index !== -1 && index < rows.length && !isTouched(rows[index])) {
			run.push(rows[index].ordinal)
			index++
		}
		const positions = nodes
			.map((node, position) => node.kind === 'stop' && run.includes(node.row.ordinal) ? position : -1)
			.filter((position) => position !== -1)
		return {first, from: Math.min(...positions), to: Math.max(...positions)}
	}).filter((range) => Number.isFinite(range.from))
}

const nodeY = (node: LineNode): number => {
	if (node.kind === 'removed') {
		return LINE_Y + DEVIATION
	}
	if (node.kind === 'stop' && (node.row.change?.type === 'ADD' || node.row.change?.type === 'MOVE')) {
		return LINE_Y - DEVIATION
	}
	return LINE_Y
}

/** One smooth line through the stops: it bends up to a stop the change pulled off it. */
const linePath = (points: { x: number, y: number }[]): string => {
	if (!points.length) {
		return ''
	}
	return points.reduce((path, point, index) => {
		if (index === 0) {
			return `M ${point.x - 20} ${LINE_Y} L ${point.x} ${point.y}`
		}
		const previous = points[index - 1]
		const middle = (previous.x + point.x) / 2
		return `${path} C ${middle} ${previous.y} ${middle} ${point.y} ${point.x} ${point.y}`
	}, '') + ` L ${points[points.length - 1].x + 20} ${LINE_Y}`
}

/** The bend between two neighbouring stops, as drawn by {@link linePath}. */
const segmentPath = (from: { x: number, y: number }, to: { x: number, y: number }): string => {
	const middle = (from.x + to.x) / 2
	return `M ${from.x} ${from.y} C ${middle} ${from.y} ${middle} ${to.y} ${to.x} ${to.y}`
}

const CIRCLE_COLOR: Partial<Record<SyncChangeTypeVoyageTemplate, string>> = {
	ADD: STOP_COLOR_SYNC.added,
	MOVE: STOP_COLOR_SYNC.changed,
	UPDATE: STOP_COLOR_SYNC.changed,
}

interface ILineProps {
	diff: IStopsDiff
	/** the stop under the cursor, so the map can point it out */
	onHighlight?: (stop: IHighlightedStopSync | null) => void
}

interface IProps {
	diff: IStopsDiff
}

/**
 * The route as a line of stops, read left to right, in the map's colours: untouched stops sit on
 * the line, an added (yellow) or moved (orange) stop pulls the line up to it, a removed one (red)
 * hangs below it.
 * Runs of untouched stops fold into one node.
 */
const StopsLineSyncVoyageTemplate = ({diff, onHighlight}: ILineProps) => {

	const {t} = useTranslation()

	const [expanded, setExpanded] = useState<number[]>([])

	const nodes = useMemo(() => {
		return foldUntouched(placeRemoved(diff.rows, diff.removed), expanded)
	}, [diff, expanded])

	const brackets = useMemo(() => expandedRanges(nodes, diff.rows, expanded), [nodes, diff, expanded])

	const x = (position: number) => PAD_X + position * STEP
	const width = Math.max(x(nodes.length - 1) + PAD_X, 200)

	const mainNodes = nodes
		.map((node, position) => ({node, point: {x: x(position), y: nodeY(node)}}))
		.filter(({node}) => node.kind !== 'removed')
	const mainPoints = mainNodes.map(({point}) => point)

	// the stretch into and out of a changed stop takes the stop's colour, like the route on the map
	const nodeColor = (node: LineNode) => node.kind === 'stop' ? CIRCLE_COLOR[node.row.change?.type] : undefined
	const coloredSegments = mainNodes.slice(1)
		.map(({node, point}, index) => ({
			from: mainNodes[index].point,
			to: point,
			color: nodeColor(node) ?? nodeColor(mainNodes[index].node),
		}))
		.filter((segment) => segment.color)

	const label = (node: LineNode): string => {
		if (node.kind === 'stop') {
			return node.row.name
		}
		if (node.kind === 'removed') {
			return node.removed.name
		}
		return t('singleVoyage.voyageTemplate.sync.unchangedShort', {count: node.rows.length})
	}

	return (
		<Box sx={sx.scroller}>
			<Box sx={{...sx.canvas, width, height: HEIGHT}}>
				<svg width={width} height={HEIGHT} style={{position: 'absolute', left: 0, top: 0}}>
					<path d={linePath(mainPoints)} fill={'none'} stroke={STOP_COLOR_SYNC.line} strokeWidth={4}
						  strokeLinecap={'round'}/>

					{coloredSegments.map((segment, index) => <path
						key={`segment-${index}`}
						d={segmentPath(segment.from, segment.to)}
						fill={'none'} stroke={segment.color} strokeWidth={4} strokeLinecap={'round'}
					/>)}

					{nodes.map((node, position) => node.kind === 'removed' && <line
						key={`stub-${position}`}
						x1={x(position)} y1={LINE_Y} x2={x(position)} y2={nodeY(node) - RADIUS}
						stroke={STOP_COLOR_SYNC.removed} strokeWidth={2} strokeDasharray={'3 3'}
					/>)}

					{brackets.map((bracket) => <path
						key={`bracket-${bracket.first}`}
						d={`M ${x(bracket.from) - 14} ${LINE_Y - 30} V ${LINE_Y - 36} H ${x(bracket.to) + 14} V ${LINE_Y - 30}`}
						fill={'none'} stroke={STOP_COLOR_SYNC.muted} strokeWidth={1}
					/>)}
				</svg>

				{brackets.map((bracket) => (
					<Typography
						key={`collapse-${bracket.first}`}
						sx={{...sx.collapse, left: (x(bracket.from) + x(bracket.to)) / 2, top: LINE_Y - 50}}
						onClick={() => setExpanded((prev) => prev.filter((it) => it !== bracket.first))}
					>
						{t('singleVoyage.voyageTemplate.sync.collapse')}
					</Typography>
				))}

				{nodes.map((node, position) => {
					const left = x(position)
					const top = nodeY(node)

					if (node.kind === 'folded') {
						return (
							<React.Fragment key={`folded-${node.rows[0].ordinal}`}>
								<Box
									sx={{...sx.circle, ...sx.circleFolded, left, top}}
									onClick={() => setExpanded((prev) => [...prev, node.rows[0].ordinal])}
									title={t('singleVoyage.voyageTemplate.sync.unchangedStops', {
										count: node.rows.length,
										from: node.rows[0].ordinal + 1,
										to: node.rows[node.rows.length - 1].ordinal + 1,
									})}
								>
									⋯
								</Box>
								<Typography
									sx={{...sx.label, ...sx.labelFolded, left}}
									onClick={() => setExpanded((prev) => [...prev, node.rows[0].ordinal])}
								>
									{label(node)}
								</Typography>
							</React.Fragment>
						)
					}

					const isRemoved = node.kind === 'removed'
					const change = node.kind === 'stop' ? node.row.change : null
					const background = isRemoved ? 'transparent' : (CIRCLE_COLOR[change?.type] ?? STOP_COLOR_SYNC.stop)
					const text = isRemoved ? '—' : String(node.row.ordinal + 1)
					const wasOrdinal = node.kind === 'stop' ? node.row.wasOrdinal : null
					const hover = {
						onMouseEnter: () => onHighlight?.(isRemoved
							? (node.removed.wasOrdinal === null ? null : {ordinal: node.removed.wasOrdinal, old: true})
							: {ordinal: node.row.ordinal}),
						onMouseLeave: () => onHighlight?.(null),
					}

					return (
						<React.Fragment key={isRemoved ? `removed-${position}` : `stop-${node.row.ordinal}`}>
							{wasOrdinal !== null && <Typography sx={{...sx.was, left, top: top - 30}}>
								{t('singleVoyage.voyageTemplate.sync.wasNumber', {number: wasOrdinal + 1})}
                            </Typography>}

							<Tooltip title={label(node)} placement={'top'}>
								<Box {...hover} sx={{
									...sx.circle,
									left,
									top,
									bgcolor: background,
									color: change && change.type !== 'ADD' ? '#fff' : (isRemoved ? STOP_COLOR_SYNC.removed : '#222'),
									...(!change && !isRemoved ? sx.circleUnchanged : {}),
									...(isRemoved ? sx.circleRemoved : {}),
								}}>
									{text}
								</Box>
							</Tooltip>

							<Typography {...hover} sx={{...sx.label, left, ...(isRemoved ? sx.labelRemoved : {})}}>
								{label(node)}
							</Typography>
						</React.Fragment>
					)
				})}
			</Box>
		</Box>
	);
};

/** The before/after of every stop the sync rewrites, one line each, under the stop line. */
export const StopChangesListSyncVoyageTemplate = ({diff}: IProps) => {

	const {t} = useTranslation()

	const value = (detail: ISyncChangeDetailVoyageTemplate, raw: string | null): string => {
		if (raw === null || raw === '') {
			return '—'
		}
		if (detail.field === 'RADIUS') {
			return t('singleVoyage.voyageTemplate.sync.detail.metres', {value: raw})
		}
		if (detail.field === 'TYPE') {
			return t(`singleVoyage.voyageTemplate.sync.detail.types.${raw}`, {defaultValue: raw})
		}
		return raw
	}

	const rows = diff.rows.filter((row) => row.change?.distanceMeters || row.change?.details?.length)

	if (!rows.length) {
		return <></>
	}

	return (
		<Stack sx={sx.changes}>
			{rows.map((row) => (
				<Typography key={row.ordinal} sx={sx.changeLine}>
					<Box component={'span'} sx={sx.changeNumber}>№{row.ordinal + 1}</Box>
					{row.change.type === 'MOVE' && !!row.change.distanceMeters &&
						<Box component={'span'} sx={sx.changePart}>
							{t('singleVoyage.voyageTemplate.sync.detail.moved', {value: row.change.distanceMeters})}
						</Box>}
					{row.change.details?.map((detail) => (
						<Box component={'span'} sx={sx.changePart} key={detail.field}>
							{detail.field === 'TIMES'
								? t('singleVoyage.voyageTemplate.sync.detail.TIMES')
								: <>
									{t(`singleVoyage.voyageTemplate.sync.detail.${detail.field}`)}:{' '}
									<Box component={'span'} sx={sx.before}>{value(detail, detail.before)}</Box>
									{' → '}
									<Box component={'span'} sx={sx.after}>{value(detail, detail.after)}</Box>
								</>}
						</Box>
					))}
				</Typography>
			))}
		</Stack>
	)
}

export default StopsLineSyncVoyageTemplate;
